import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  constants,
  SiteConfigManager,
  useApplicationState,
  useDetection,
  usePaymentMethods,
} from '@atlas/core';
import { Button, Icon, Label, Loader, MoneyInput } from '@atlas/ui';

import { QUICK_DEPOSIT_POPUP_TITLE_TYPE } from 'src/common/constants';
import { useQuickDepositBonusesContext } from 'src/common/Popup/QuickDepositPopup/QuickDepositBonusesProvider';
import QuickDepositPaymentMethod from 'src/common/Popup/QuickDepositPopup/QuickDepositPaymentMethod';
import { useSignupPopup } from 'src/components/Auth/useSignUpPopup';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import useFormatter from 'src/hooks/useFormatter';

import { useDepositSubmit } from './hooks/useDepositSubmit';
import { useInitialPayment } from './hooks/useInitialPayment';
import QuickDepositBonus from './QuickDepositBonus';
import QuickDepositPaymentMethodsPopup from './QuickDepositPaymentMethodsPopup';

export const BUTTON_POSITION = {
  BOTTOM: 'bottom',
  INLINE: 'inline',
};

const QuickDepositForm = ({
  buttonPosition = BUTTON_POSITION.INLINE,
  closeQRCodeOnSuccess,
  onClose,
  onDepositStart,
  onDepositSuccess,
  setPaymentUrl,
  setRedirectUrl,
  titleType = QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT,
}) => {
  const {
    wallet: { currency },
  } = useApplicationState();
  const history = useHistory();

  const { currencyFormatter } = useFormatter();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();
  const { paymentMethods } = usePaymentMethods(
    constants.PAYMENT_OPERATION.DEPOSIT,
    constants.DEVICE_TYPES.MOBILE,
  );

  const { browser, ip, locale, os } = useDetection();
  const showInPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);
  const { openSignupPopup } = useSignupPopup();

  const [isInputActive, setIsInputActive] = useState(false);
  const [value, setValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { initialValue, paymentMethod, setPaymentMethod } = useInitialPayment(paymentMethods);

  useEffect(() => {
    if (initialValue && value === '') {
      setValue(initialValue);
    }
  }, [initialValue, value]);

  const {
    activatedBonus,
    amountToGetBonus,
    bonusesList,
    depositBonuses,
    isBonusesDisabled,
    selectedBonus,
    setInputValue,
    setPaymentMethod: setBonusesPaymentMethod,
    setSelectedBonus,
    showBonus,
  } = useQuickDepositBonusesContext();

  const { error, handleDeposit, loading, setError } = useDepositSubmit({
    activatedBonus,
    browser,
    closeQRCodeOnSuccess,
    history,
    ip,
    locale,
    onClose,
    onDepositStart,
    onDepositSuccess,
    openSignupPopup,
    os,
    paymentMethod,
    setPaymentUrl,
    setRedirectUrl,
    showInPopup,
    showSelfRegulationPopupIfNeeded,
    showSpentLimitPopupIfNeeded,
    value,
  });

  useEffect(() => {
    if (!paymentMethod || !value) return;
    const numValue = Number(value);
    if (!numValue || Number.isNaN(numValue)) return;

    if (paymentMethod.minAmount && numValue < paymentMethod.minAmount) {
      setValue(paymentMethod.minAmount);
      setError(null);
    } else if (paymentMethod.maxAmount && numValue > paymentMethod.maxAmount) {
      setValue(paymentMethod.maxAmount);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  const disableButton = !value || loading;

  const handleValueChange = value => {
    setValue(value);
    setInputValue(value);

    if (error) {
      setError(null);
    }
  };

  const handleBlur = useCallback(() => {
    setIsInputActive(false);
    if (!paymentMethod || !value) return;
    const numValue = Number(value);
    if (!numValue || Number.isNaN(numValue)) return;

    if (paymentMethod.maxAmount && numValue > paymentMethod.maxAmount) {
      setValue(paymentMethod.maxAmount);
    } else if (paymentMethod.minAmount && numValue < paymentMethod.minAmount) {
      setValue(paymentMethod.minAmount);
    }
  }, [paymentMethod, value]);

  const handleSavePaymentMethod = useCallback(
    paymentMethod => {
      setPaymentMethod(paymentMethod);
      setBonusesPaymentMethod(paymentMethod);
      setIsDropdownOpen(false);
    },
    [setPaymentMethod, setBonusesPaymentMethod],
  );

  useEffect(() => {
    if (paymentMethod) {
      setBonusesPaymentMethod(paymentMethod);
    }
  }, [paymentMethod, setBonusesPaymentMethod]);

  const handlePaymentMethodsClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  return (
    <>
      <div className="quick-deposit-content">
        {paymentMethod ? (
          <>
            <div className="quick-deposit-form">
              <div className="quick-deposit-input">
                <QuickDepositPaymentMethod
                  isDropdownOpen={isDropdownOpen}
                  paymentMethod={paymentMethod}
                  setIsDropdownOpen={setIsDropdownOpen}
                  withDropdown={paymentMethods.length > 1}
                />
                <MoneyInput
                  active={isInputActive}
                  currency={currency}
                  error={error}
                  label="amount"
                  onBlur={handleBlur}
                  onChange={handleValueChange}
                  onFocus={() => setIsInputActive(true)}
                  value={value}
                />
                {buttonPosition === BUTTON_POSITION.INLINE && (
                  <Button
                    className="deposit-button"
                    disabled={disableButton}
                    label="deposit"
                    loading={loading}
                    onClick={handleDeposit}
                    size="big"
                  />
                )}
              </div>
              {error && (
                <div className="quick-deposit-form-error">
                  <Icon className="error-icon" height={18} name="error" width={18} />
                  <Label className="error-message" message={error.message} />
                </div>
              )}
            </div>
            {amountToGetBonus > 0 && (
              <Label
                className="quick-deposit-bonus-info"
                message="quick_deposit_add_amount_to_get_bonus"
                params={{
                  amount: currencyFormatter.format(amountToGetBonus),
                }}
              />
            )}
            {showBonus && (
              <QuickDepositBonus
                bonuses={activatedBonus ?? bonusesList}
                isBonusesDisabled={isBonusesDisabled}
                onSelectBonus={setSelectedBonus}
                selectedBonus={selectedBonus}
                titleType={titleType}
              />
            )}
            {buttonPosition === BUTTON_POSITION.BOTTOM && (
              <Button
                className="deposit-button"
                disabled={disableButton}
                label="deposit"
                loading={loading}
                onClick={handleDeposit}
                size="big"
              />
            )}
          </>
        ) : (
          <Loader />
        )}
      </div>
      {isDropdownOpen && (
        <QuickDepositPaymentMethodsPopup
          depositBonuses={depositBonuses}
          handleClose={handlePaymentMethodsClose}
          handleSavePaymentMethod={handleSavePaymentMethod}
          paymentMethod={paymentMethod}
          paymentMethods={paymentMethods}
        />
      )}
    </>
  );
};

QuickDepositForm.propTypes = {
  buttonPosition: PropTypes.oneOf(['inline', 'bottom']),
  closeQRCodeOnSuccess: PropTypes.bool,
  onClose: PropTypes.func,
  onDepositStart: PropTypes.func,
  onDepositSuccess: PropTypes.func,
  setPaymentUrl: PropTypes.func.isRequired,
  setRedirectUrl: PropTypes.func,
  titleType: PropTypes.oneOfType([
    QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT,
    QUICK_DEPOSIT_POPUP_TITLE_TYPE.INSUFFICIENT,
  ]),
};

export default QuickDepositForm;
